import { motion } from "framer-motion";
import { Flame } from "lucide-react";
import { SectionHeading, reveal } from "./SectionHeading";
import { FloatingGhost } from "./Ghost";

const milestones = [
  {
    phase: "Phase 01 // Midnight Awakening",
    title: "The Awakening & FOMO App Launch",
    status: "In Progress",
    live: true,
    deliverables: [
      "Contract deployment & stealth fair launch",
      "Initial liquidity burn to the void",
      "FOMO app trending domination campaign",
      "DexScreener & DexTools verification",
    ],
  },
  {
    phase: "Phase 02 // Coven Gathering",
    title: "Community Coven & Viral Invasions",
    status: "Upcoming",
    live: false,
    deliverables: [
      "Haunted meme competition with pumpkin prize pool",
      "Partnerships with leading Halloween meme tastemakers",
      "CoinGecko & CoinMarketCap fast-track listings",
      "Discord bot mini-game: Trick or Snipe",
    ],
  },
  {
    phase: "Phase 03 // The Harvest",
    title: "Seasonal Utility & Next Sister Projects",
    status: "Upcoming",
    live: false,
    deliverables: [
      "Exclusive FOMOWEEN Spirits generative collectible drop",
      "Preview of the next season memecoin series by the creators",
      "Community DAO voting for seasonal vault distributions",
      "The annual All Hallows Eve burn celebration",
    ],
  },
];

export default function Roadmap() {
  return (
    <section
      id="roadmap"
      data-testid="roadmap-section"
      className="relative px-6 py-28 sm:px-12 sm:py-36"
    >
      <FloatingGhost className="right-[8%] top-[12%] hidden xl:block" size={60} opacity={0.35} />

      <div className="mx-auto max-w-5xl">
        <SectionHeading
          eyebrow="Chapter III // Nightmare Roadmap"
          title="What Rises"
          accent="Next From the Crypt"
          description="Three phases between here and the harvest. Each one drags more souls into the coven."
          testid="roadmap-heading"
        />

        <div className="relative">
          <div className="absolute left-4 top-0 h-full w-px bg-gradient-to-b from-pumpkin via-purple-500/40 to-transparent lg:left-1/2" />

          {milestones.map((m, i) => (
            <div key={m.phase} className="relative mb-14 last:mb-0 lg:mb-20">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className={`absolute left-4 top-3 h-4 w-4 -translate-x-1/2 rounded-full lg:left-1/2 ${
                  m.live
                    ? "bg-pumpkin shadow-[0_0_18px_rgba(255,107,0,0.9)]"
                    : "border border-purple-400/60 bg-elevated"
                }`}
              />
              <motion.article
                data-testid={`roadmap-phase-${i + 1}`}
                {...reveal(0.1)}
                className={`crypt-card ml-12 lg:ml-0 lg:w-[calc(50%-3rem)] ${
                  i % 2 === 1 ? "lg:ml-[calc(50%+3rem)]" : ""
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.3em] text-orange-400/80">
                    {m.phase}
                  </p>
                  <span
                    className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest ${
                      m.live
                        ? "border border-orange-500/50 bg-pumpkin/15 text-orange-300"
                        : "border border-purple-500/40 bg-purple-500/10 text-purple-300"
                    }`}
                  >
                    {m.status}
                  </span>
                </div>
                <h3 className="mt-4 font-syne text-lg font-extrabold uppercase tracking-tight text-zinc-100 sm:text-xl">
                  {m.title}
                </h3>
                <ul className="mt-5 space-y-3">
                  {m.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-3 text-sm leading-relaxed text-zinc-400">
                      <Flame size={14} className="mt-1 shrink-0 text-pumpkin" />
                      {d}
                    </li>
                  ))}
                </ul>
              </motion.article>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
